import React from 'react';
import { Camera, UserPlus, Menu } from 'lucide-react';
import { theme } from '../theme';
import { useBranch } from '../context/BranchContext';
import { LANGS, getLang, setLang } from '../i18n';

/**
 * Top header bar with breadcrumb-style page title, branch switcher, face checkin, new patient, and live date.
 */
const Header = ({ pageTitle, onFaceCheckin, onNewPatient, onSearch, onMenuClick, pharmacyMode, setPharmacyMode, pharmacyEnabled }) => {
  const { branches, activeBranch, setActiveBranch } = useBranch();

  return (
    <header style={{
      minHeight:'64px',
      backgroundColor: theme.surface,
      borderBottom: `1px solid ${theme.border}`,
      display:'flex', alignItems:'center', justifyContent:'space-between',
      padding:'8px 16px',
      gap:'12px',
      flexShrink:0,
      flexWrap:'wrap',
    }}>
      <div style={{display:'flex', alignItems:'center', gap:8, minWidth:0}}>
        {/* Mobile hamburger — hidden on desktop via CSS in index.css. The
            sidebar is `display:none` < 768px, so this is the only way for
            phone users to reach navigation. */}
        {onMenuClick && (
          <button
            className="mobile-hamburger"
            onClick={onMenuClick}
            aria-label="Open menu"
            style={{
              background:'none', border:'none', cursor:'pointer',
              padding:8, borderRadius:8, color: theme.navy,
              alignItems:'center', justifyContent:'center',
            }}
          >
            <Menu size={22}/>
          </button>
        )}
        <div>
          <div style={{fontSize:'0.72rem', color:theme.textSubtle, fontWeight:'600', letterSpacing:'0.04em', textTransform:'uppercase'}}>Workspace</div>
          <h1 style={{margin:0, fontSize:'1.15rem', color:theme.navy, fontWeight:'700'}}>{pageTitle}</h1>
        </div>
      </div>

      <div style={{display:'flex', alignItems:'center', gap:'14px', flexWrap:'wrap', justifyContent:'flex-end', minWidth:0}}>
        {/* Language switcher */}
        <select
          defaultValue={getLang()}
          onChange={(e) => { setLang(e.target.value); }}
          title="UI Language"
          style={{
            padding:'6px 10px', borderRadius: theme.radiusSm,
            border:`1px solid ${theme.border}`, background: theme.bg,
            fontSize:'0.8rem', color: theme.navy, cursor:'pointer',
          }}
        >
          {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>

        {/* Search */}
        {onSearch && (
          <button onClick={onSearch}
            style={{
              display:'flex', alignItems:'center', gap:'8px',
              padding:'8px 18px',
              backgroundColor: theme.bg,
              border:`1px solid ${theme.border}`,
              borderRadius: theme.radiusSm,
              cursor:'pointer', fontSize:'0.82rem', color:theme.textMuted,
            }}>
            🔍 Search... <kbd className="kbd-shortcut-hint" style={{padding:'1px 6px', backgroundColor:'#e9ecef', borderRadius:'3px', fontSize:'0.68rem', color:'#888', border:'1px solid #ddd', marginLeft:'4px'}}>⌘K</kbd>
          </button>
        )}

        {/* New Patient */}
        {onNewPatient && (
          <button onClick={onNewPatient}
            style={{
              display:'flex', alignItems:'center', gap:'6px',
              padding:'8px 16px',
              backgroundColor:theme.success,
              color:'white', border:'none',
              borderRadius: theme.radiusSm,
              cursor:'pointer', fontWeight:'600', fontSize:'0.82rem',
              boxShadow:'0 2px 8px rgba(16,185,129,0.3)',
            }}>
            <UserPlus size={16}/> New Patient
          </button>
        )}

        {/* Face Check-in */}
        {onFaceCheckin && (
          <button onClick={onFaceCheckin}
            style={{
              display:'flex', alignItems:'center', gap:'6px',
              padding:'8px 16px',
              background:`linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
              color:'white', border:'none',
              borderRadius: theme.radiusSm,
              cursor:'pointer', fontWeight:'600', fontSize:'0.82rem',
              boxShadow:'0 2px 8px rgba(0,181,184,0.3)',
            }}>
            <Camera size={16}/> Face Check-in
          </button>
        )}

        {/* Branch / Module switcher */}
        <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
          <span style={{fontSize:'0.72rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.04em'}}>
            {pharmacyMode ? 'Module' : 'Branch'}
          </span>
          <select
            value={pharmacyMode ? '__pharmacy__' : (activeBranch?._id || '')}
            onChange={(e) => {
              if (e.target.value === '__pharmacy__') {
                setPharmacyMode(true);
              } else {
                setPharmacyMode(false);
                const next = branches.find(b => b._id === e.target.value);
                if (next) setActiveBranch(next);
              }
            }}
            style={{
              padding:'8px 14px',
              backgroundColor: pharmacyMode ? '#fff7ed' : theme.bg,
              border: pharmacyMode ? '2px solid #f59e0b' : `1px solid ${theme.border}`,
              borderRadius: theme.radiusSm,
              fontSize:'0.85rem',
              color: pharmacyMode ? '#92400e' : theme.navy,
              fontWeight:'700',
              cursor:'pointer',
            }}>
            {branches.map(b => (
              <option key={b._id} value={b._id}>
                🏥 {b.name}{b.isPrimary ? ' ★' : ''}
              </option>
            ))}
            {pharmacyEnabled && (
              <option value="__pharmacy__">💊 Pharmacy</option>
            )}
          </select>
        </div>

        <div style={{
          padding:'8px 14px',
          backgroundColor: theme.bg,
          border:`1px solid ${theme.border}`,
          borderRadius: theme.radiusSm,
          fontSize:'0.82rem',
          color: theme.textMuted,
          fontWeight:'500',
        }}>
          {new Date().toLocaleDateString('en-IN',{weekday:'long', day:'numeric', month:'long', year:'numeric'})}
        </div>
      </div>
    </header>
  );
};

export default Header;
